import { DialogClose } from "@radix-ui/react-dialog"
import { VisuallyHidden } from "@radix-ui/react-visually-hidden"
import { useCallback, useState, useMemo } from "react"

import {
  DialogContent,
  DialogDescription,
  DialogFooterV2,
  DialogHeaderV2,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "~/components/reusable/Dialog"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import { Checkbox } from "~/components/design-system/components"
import { hideBlockedByErpMessage, hideNeedsCollectionRemovalMessage } from "~/constants/npi"
import { useFilters } from "~/hooks/useFilters"
import { useSaveMetadata } from "~/hooks/useMetadata"
import { trackEvent } from "~/hooks/useTrackingService"
import { productTypeOptions } from "~/models/enum"
import { api } from "~/utils/api"
import { DateFormatType, formatDate } from "~/utils/datetime"
import { formatSecondaryMOQName } from "~/utils/formatting"
import {
  hasFieldChanged,
  pickLifecycleBadgeField,
  getFieldValueForSave,
  getEffectiveBufferType,
  getResetValue,
  normalizeMultipleValue,
  shouldShowResetButton as shouldShowReset,
} from "~/utils/metadataUtils"

import { Button, IconButton } from "../../reusable/Button"
import { Badge } from "../../reusable/Badge"
import InfoTooltip from "../../reusable/InfoTooltip"
import { Label } from "../../reusable/Label"
import { UserSelect } from "../../reusable/Select"
import { Switch } from "../../reusable/Switch"
import { Tooltip } from "../../reusable/Tooltip"
import { isDevelopment } from "~/utils/environment"
import { useGetUsers } from "~/hooks/useUsers"
import { Buildings, Database, Gear, Pencil, PencilSimple, User } from "@phosphor-icons/react"
import { formatName } from "~/utils/namingUtils"
import { Card } from "~/components/reusable/Card"
import { NumericFieldRow } from "./NumericFieldRow"
import { SelectFieldRow } from "./SelectFieldRow"
import { BufferFieldsGroup } from "./BufferFieldsGroup"
import { ResetButton } from "./ResetButton"
import { FieldRow } from "./FieldRow"
import { LifecycleFieldRow } from "./LifecycleFieldRow"
import { ErpIndicator } from "./ErpIndicator"
import { formatValue, formatValueList } from "./erpTooltip"
import type { FieldMetadata, IFormData, MetadataForEdit } from "./types"

const hideHelperText = (
  field: FieldMetadata | undefined,
  needsRemovalConsent: boolean,
  selectedCount: number,
): string => {
  if (field && !field.isEditable) return hideBlockedByErpMessage()
  if (needsRemovalConsent)
    return hideNeedsCollectionRemovalMessage(
      field?.metadata?.collectionNames ?? [],
      selectedCount === 1 ? 1 : undefined,
      selectedCount,
    )
  return "Hidden variants will not be visible on the platform"
}

type Props = {
  selectedSkuIds: string[]
  selectedVariantCount: number
  commonVendors: { id: string; name: string }[]
  useSettingsIcon?: boolean
  /** What the trigger says while the user IS allowed, and its accessible name when the icon
   * variant leaves it otherwise unlabelled. It rides on the gated trigger rather than a `Tooltip`
   * around it, which would open a second bubble beside the permission one. */
  allowedTitle?: string
  resetSelectedProductIds?: () => void
  triggerButtonVariant?: "neutral" | "primary"
  /** Replaces the default Edit button, for hosts with their own affordance (a menu row). */
  customTrigger?: React.ReactNode
  /** Controlled mode: renders no trigger, the host owns the open state. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Opens on the lifecycle date editor, for a host whose prompt is a missing date. */
  openLifecycle?: boolean
}

const buildInitialFormData = (
  metadataForEdit: MetadataForEdit,
  hasHiddenFilter: boolean,
): IFormData => {
  const fields = metadataForEdit.fields
  return {
    totalLeadTimeDays: normalizeMultipleValue(fields.totalLeadTimeDays?.value),
    purchaseIntervalDays: normalizeMultipleValue(fields.purchaseIntervalDays?.value),
    minimumOrderQuantity: normalizeMultipleValue(fields.minimumOrderQuantity?.value),
    secondaryMoq: normalizeMultipleValue(fields.secondaryMoq?.value),
    colliSize: normalizeMultipleValue(fields.colliSize?.value),
    bufferType: normalizeMultipleValue(fields.bufferType?.value),
    bufferValue: normalizeMultipleValue(fields.bufferValue?.value),
    lifecycleStatus: normalizeMultipleValue(fields.lifecycleStatus?.value),
    productType: normalizeMultipleValue(fields.productType?.value),
    activatedFrom: normalizeMultipleValue(fields.activatedFrom?.value),
    activatedUntil: normalizeMultipleValue(fields.activatedUntil?.value),
    phasingOutFrom: normalizeMultipleValue(fields.phasingOutFrom?.value),
    preferredVendorId: normalizeMultipleValue(fields.preferredVendorId?.value),
    userId: normalizeMultipleValue(fields.userId?.value),
    ignore: hasHiddenFilter || fields.ignore?.value === true ? true : undefined,
  }
}

type SourceBadgeProps = {
  source: "ERP" | "Custom" | "Default" | "Modified"
  tooltip?: string
}

const SourceBadge = ({ source, tooltip }: SourceBadgeProps) => {
  const badges = {
    ERP: (
      <Badge size="sm" variant="gray">
        <Database className="w-3 h-3 mr-1" />
        ERP
      </Badge>
    ),
    Modified: (
      <Badge size="sm" variant="yellow">
        <Pencil className="w-3 h-3 mr-1" />
        Unsaved
      </Badge>
    ),
    Default: (
      <Badge size="sm" variant="neutral">
        <Buildings className="w-3 h-3 mr-1" />
        Default
      </Badge>
    ),
    Custom: (
      <Badge size="sm" variant="violet">
        <User className="w-3 h-3 mr-1" />
        Custom
      </Badge>
    ),
  }

  const badge = badges[source]

  if (tooltip) {
    return <Tooltip title={tooltip}>{badge}</Tooltip>
  }
  return badge
}

/** Returns the element, not a component: `asChild` clones its child, and a component swallows the ref. */
const defaultTrigger = ({ useSettingsIcon, triggerButtonVariant, allowedTitle }: Props) => {
  if (useSettingsIcon)
    return (
      <IconButton variant="neutral" aria-label={allowedTitle ?? "Edit settings"}>
        <Gear />
      </IconButton>
    )
  return (
    <Button variant={triggerButtonVariant ?? "primary"} size="sm">
      <PencilSimple />
      Edit
    </Button>
  )
}

/** For hosts that open the dialog in controlled mode, where no trigger reports the open */
export const trackMultiEditOpened = (skuCount: number) =>
  trackEvent("SKUs Edit Dialog Opened", { "Number of SKUs": skuCount })

export default function MultiEditDialog(props: Props) {
  const [internalOpen, setInternalOpen] = useState(false)
  const isControlled = props.open !== undefined
  const open = isControlled ? props.open === true : internalOpen

  const handleOpenChange = (next: boolean) => {
    if (next) trackMultiEditOpened(props.selectedSkuIds.length)
    if (isControlled) props.onOpenChange?.(next)
    else setInternalOpen(next)
  }

  return (
    <DialogRoot open={open} onOpenChange={handleOpenChange}>
      {!isControlled && (
        <DialogTrigger requires="edit" allowedTitle={props.allowedTitle} asChild>
          {props.customTrigger ?? defaultTrigger(props)}
        </DialogTrigger>
      )}
      <DialogContent className="max-w-[595px] p-0 gap-0">
        <MultiEditDialogContent {...props} closeDialog={() => handleOpenChange(false)} />
      </DialogContent>
    </DialogRoot>
  )
}

function MultiEditDialogContent(props: Props & { closeDialog: () => void }) {
  // Mounted only while the dialog is open; gcTime: 0 drops the cache on close, so every
  // open initializes the form below from freshly fetched metadata.
  const { data: metadataForEdit } = api.product.getMetadataForEdit.useQuery(
    { skuIds: props.selectedSkuIds },
    { enabled: props.selectedSkuIds.length > 0, gcTime: 0 },
  ) as { data: MetadataForEdit | undefined }

  if (!metadataForEdit) {
    return (
      <>
        <VisuallyHidden>
          <DialogTitle>Loading metadata</DialogTitle>
          <DialogDescription>
            Please wait while we load the metadata for the selected SKUs.
          </DialogDescription>
        </VisuallyHidden>
        <LoadingOverlay />
      </>
    )
  }

  return <MultiEditDialogForm {...props} metadataForEdit={metadataForEdit} />
}

function MultiEditDialogForm({
  metadataForEdit,
  closeDialog,
  ...props
}: Props & { metadataForEdit: MetadataForEdit; closeDialog: () => void }) {
  const { filters } = useFilters()
  const { data: users } = useGetUsers(isDevelopment())

  // This component mounts once per dialog open, after the metadata has loaded, so plain
  // state initializers replace the previous effect-based init — a background refetch
  // re-renders but never resets the user's unsaved edits.
  const [originalFormData] = useState<IFormData>(() =>
    buildInitialFormData(metadataForEdit, !!filters?.find((x) => x.field === "isHidden")),
  )
  const [formData, setFormData] = useState<IFormData>(originalFormData)
  const [removeFromCollections, setRemoveFromCollections] = useState(false)

  const saveMetadata = useSaveMetadata()

  const checkFieldChanged = useCallback(
    (field: keyof IFormData) => hasFieldChanged(field, formData, originalFormData),
    [formData, originalFormData],
  )

  const hideCollectionNames = metadataForEdit.fields.ignore?.metadata?.collectionNames ?? []
  const needsRemovalConsent =
    formData.ignore === true && checkFieldChanged("ignore") && hideCollectionNames.length > 0

  const bufferValueMissing =
    checkFieldChanged("bufferType") &&
    formData.bufferType !== undefined &&
    formData.bufferValue === undefined
  let saveBlockedReason: string | undefined
  if (bufferValueMissing) {
    saveBlockedReason =
      "You need to type in a value for the buffer value, if you have selected a safety stock type."
  } else if (needsRemovalConsent && !removeFromCollections) {
    saveBlockedReason = "Confirm that the variants are also removed from their collection."
  }

  const handleUpdateMetadata = async () => {
    if (props.selectedSkuIds) {
      trackEvent("SKUs Edited", {
        "Number of SKUs": props.selectedSkuIds.length,
        "Lead Time": formData.totalLeadTimeDays,
        "Purchase Interval": formData.purchaseIntervalDays,
        "Product Hidden": formData.ignore,
        "Minimum Order Quantity": formData.minimumOrderQuantity,
      })

      const getFieldValue = <K extends keyof IFormData>(field: K) =>
        getFieldValueForSave(field, formData, originalFormData, metadataForEdit)

      // The buffer pair is semantically atomic: a bufferValue is interpreted through its
      // bufferType. In bulk selections the stored types can differ per SKU (or be unset),
      // so saving a value alone would leave it inert on part of the selection. Pin the
      // type the dialog displayed whenever a value is saved.
      const bufferValue = getFieldValue("bufferValue")
      let bufferType = getFieldValue("bufferType")
      if (bufferValue != null && bufferType === undefined) {
        bufferType = getEffectiveBufferType(formData, metadataForEdit)
      }

      saveMetadata.mutate(
        {
          skuIds: props.selectedSkuIds,
          totalLeadTimeDays: getFieldValue("totalLeadTimeDays"),
          purchaseIntervalDays: getFieldValue("purchaseIntervalDays"),
          minimumOrderQuantity: getFieldValue("minimumOrderQuantity"),
          bufferType,
          bufferValue,
          colliSize: getFieldValue("colliSize"),
          ignore: checkFieldChanged("ignore") ? formData.ignore : undefined,
          removeFromCollections: needsRemovalConsent ? removeFromCollections : undefined,
          lifecycleStatus: getFieldValue("lifecycleStatus"),
          productType: getFieldValue("productType"),
          secondaryMoq: getFieldValue("secondaryMoq"),
          userId: getFieldValue("userId"),
          preferredVendorId: getFieldValue("preferredVendorId"),
          activatedFrom: getFieldValue("activatedFrom"),
          activatedUntil: getFieldValue("activatedUntil"),
          phasingOutFrom: getFieldValue("phasingOutFrom"),
        },
        {
          onSuccess: () => {
            props.resetSelectedProductIds?.()
            closeDialog()
          },
        },
      )
    }
  }

  const getFieldBadges = useCallback(
    (fieldName: keyof IFormData) => {
      if (!metadataForEdit) return null

      const fieldData = metadataForEdit.fields[fieldName]
      if (!fieldData) return null

      const fieldChanged = checkFieldChanged(fieldName)

      // PRIORITY 1: Show "Unsaved" if field was modified
      if (fieldChanged) {
        return <SourceBadge source="Modified" />
      }

      // Helper to get tooltip for source badge
      const getTooltip = (): string | undefined => {
        const erpValue = fieldData.metadata?.erpValue
        const erpValues = fieldData.metadata?.erpValues
        const defaultValue = fieldData.metadata?.defaultValue

        if (fieldData.source === "Custom") {
          return "Custom value overrides ERP or company default"
        }

        if (fieldData.source === "ERP") {
          if (erpValues && erpValues.length > 1) {
            return `Multiple ERP values: ${formatValueList(erpValues)}`
          }
          if (erpValue != null) {
            return `ERP value: ${formatValue(erpValue)}`
          }
          return "Managed by ERP"
        }

        if (fieldData.source === "Default" && defaultValue != null) {
          return `Default: ${formatValue(defaultValue)}`
        }

        return undefined
      }

      if (!fieldData.source) return null

      // PRIORITY 2-4: Show single badge based on source (Custom > ERP > Default)
      return <SourceBadge source={fieldData.source} tooltip={getTooltip()} />
    },
    [metadataForEdit, checkFieldChanged],
  )

  const shouldShowResetButton = useCallback(
    (fieldName: keyof IFormData): boolean =>
      shouldShowReset(fieldName, formData, originalFormData, metadataForEdit),
    [metadataForEdit, formData, originalFormData],
  )

  const lifecycleBadgeField = pickLifecycleBadgeField(formData, metadataForEdit, checkFieldChanged)

  const selectedProductTypeOptions = useMemo(() => {
    return productTypeOptions.find((s) => s.value === formData.productType) ?? null
  }, [formData.productType])

  const isNoos =
    formData.productType === "noos" ||
    (!formData.productType && metadataForEdit?.fields.productType?.value === "noos")

  const selectedPreferredVendor = useMemo(() => {
    const selectedVendor = props.commonVendors?.find((v) => v.id === formData.preferredVendorId)
    return selectedVendor ? { value: selectedVendor.id, label: selectedVendor.name } : null
  }, [formData.preferredVendorId, props.commonVendors])

  const handleResetToDefault = useCallback(
    (fieldName: keyof IFormData) => {
      if (!metadataForEdit?.fields[fieldName]) return
      setFormData((prev) => ({
        ...prev,
        [fieldName]: getResetValue(fieldName, originalFormData, metadataForEdit),
      }))
    },
    [metadataForEdit, originalFormData],
  )

  const getResetTooltip = useCallback(
    (fieldName: keyof IFormData): string => {
      const fieldData = metadataForEdit?.fields[fieldName]
      if (!fieldData) return "Clear custom value"

      const erpValue = fieldData.metadata?.erpValue
      const defaultValue = fieldData.metadata?.defaultValue

      const formatValue = (val: unknown): string => {
        if (val instanceof Date) {
          return formatDate(val, DateFormatType.FullDateShort)
        }
        return String(val)
      }

      if (fieldData.source !== "Custom") {
        const original = originalFormData[fieldName]
        if (original == null) return "Revert change"
        if (fieldData.source === "ERP") return `Revert to ERP value: ${formatValue(original)}`
        if (fieldData.source === "Default") {
          return `Revert to company default: ${formatValue(original)}`
        }
        return `Revert to ${formatValue(original)}`
      }

      if (erpValue !== undefined && erpValue !== null) {
        return `Reset to ERP value: ${formatValue(erpValue)}`
      }
      if (defaultValue !== undefined && defaultValue !== null) {
        return `Reset to company default: ${formatValue(defaultValue)}`
      }
      return "Clear custom value"
    },
    [metadataForEdit, originalFormData],
  )

  const getErpIndicator = useCallback(
    (fieldName: keyof IFormData, unit?: string) => {
      const fieldData = metadataForEdit?.fields[fieldName]
      if (!fieldData) return null

      return (
        <ErpIndicator
          erpValue={fieldData.metadata?.erpValue}
          erpValues={fieldData.metadata?.erpValues}
          unit={unit}
        />
      )
    },
    [metadataForEdit],
  )

  return (
    <>
      <DialogHeaderV2>
        <DialogTitle>Variant settings</DialogTitle>
        <DialogDescription>
          Edit the fields you want to change. Unchanged fields will remain as they are. Clear a
          field to remove its value.
        </DialogDescription>
      </DialogHeaderV2>

      <div className="flex flex-col gap-3 px-5 py-3 max-h-[70vh] overflow-y-scroll">
        <div className="flex flex-col bg-medium rounded-xl p-1">
          <div className="grid grid-cols-[1fr_100px_82px_185px] px-4 py-3">
            <div className="label-sm text-fg-subtle font-medium pr-3">Field</div>
            <div className="label-sm text-fg-subtle font-medium text-right border-base px-3"></div>
            <div className="label-sm text-fg-subtle font-medium text-left border-base px-3">
              ERP
            </div>
            <div className="label-sm text-fg-subtle font-medium pl-3">Value</div>
          </div>

          <Card className="flex flex-col max-h-[600px] overflow-auto">
            <FieldRow
              label={formatName("planner", false, true)}
              isFirst
              badges={getFieldBadges("userId")}
              erpIndicator={getErpIndicator("userId")}
            >
              <div className="flex items-center gap-2 w-full">
                <div className="w-full">
                  <UserSelect
                    users={users ?? []}
                    selectedUser={users?.find((u) => u.email === formData.userId) ?? undefined}
                    onChange={(u) => {
                      setFormData({ ...formData, userId: u?.value })
                    }}
                    isClearable
                    placeholder="Select a user"
                    size="sm"
                  />
                </div>
                {shouldShowResetButton("userId") && (
                  <ResetButton
                    onClick={() => handleResetToDefault("userId")}
                    tooltip={getResetTooltip("userId")}
                  />
                )}
              </div>
            </FieldRow>

            <NumericFieldRow
              label="Total lead time"
              fieldName="totalLeadTimeDays"
              formData={formData}
              setFormData={setFormData}
              metadataForEdit={metadataForEdit}
              getFieldBadges={getFieldBadges}
              shouldShowResetButton={shouldShowResetButton}
              handleResetToDefault={handleResetToDefault}
              unit="days"
            />

            <NumericFieldRow
              label="Purchase interval"
              fieldName="purchaseIntervalDays"
              formData={formData}
              setFormData={setFormData}
              metadataForEdit={metadataForEdit}
              getFieldBadges={getFieldBadges}
              shouldShowResetButton={shouldShowResetButton}
              handleResetToDefault={handleResetToDefault}
              unit="days"
            />

            <NumericFieldRow
              label="SKU MOQ"
              fieldName="minimumOrderQuantity"
              formData={formData}
              setFormData={setFormData}
              metadataForEdit={metadataForEdit}
              getFieldBadges={getFieldBadges}
              shouldShowResetButton={shouldShowResetButton}
              handleResetToDefault={handleResetToDefault}
              unit="units"
              clearButton
              allowZero
            />

            <NumericFieldRow
              label={formatSecondaryMOQName()}
              fieldName="secondaryMoq"
              formData={formData}
              setFormData={setFormData}
              metadataForEdit={metadataForEdit}
              getFieldBadges={getFieldBadges}
              shouldShowResetButton={shouldShowResetButton}
              handleResetToDefault={handleResetToDefault}
              unit="units"
              clearButton
              allowZero
            />

            <NumericFieldRow
              label="Colli Size"
              fieldName="colliSize"
              formData={formData}
              setFormData={setFormData}
              metadataForEdit={metadataForEdit}
              getFieldBadges={getFieldBadges}
              shouldShowResetButton={shouldShowResetButton}
              handleResetToDefault={handleResetToDefault}
              unit="units"
            />

            {metadataForEdit && (
              <BufferFieldsGroup
                formData={formData}
                setFormData={setFormData}
                metadataForEdit={metadataForEdit}
                getFieldBadges={getFieldBadges}
                shouldShowResetButton={shouldShowResetButton}
                handleResetToDefault={handleResetToDefault}
              />
            )}

            <LifecycleFieldRow
              formData={formData}
              setFormData={setFormData}
              metadataForEdit={metadataForEdit}
              isNoos={isNoos}
              badges={getFieldBadges(lifecycleBadgeField)}
              erpIndicator={getErpIndicator("lifecycleStatus")}
              getFieldBadges={getFieldBadges}
              shouldShowResetButton={shouldShowResetButton}
              getResetTooltip={getResetTooltip}
              handleResetToDefault={handleResetToDefault}
              defaultOpen={props.openLifecycle}
            />

            <SelectFieldRow
              label="Product type"
              value={selectedProductTypeOptions}
              onChange={(option) =>
                setFormData({ ...formData, productType: option?.value ?? null })
              }
              options={productTypeOptions}
              placeholder={
                metadataForEdit?.fields.productType?.isMultiple && formData.productType !== null
                  ? "Mixed values"
                  : undefined
              }
              fieldData={metadataForEdit?.fields.productType}
              badges={getFieldBadges("productType")}
              erpIndicator={getErpIndicator("productType")}
              showResetButton={shouldShowResetButton("productType")}
              resetTooltip={getResetTooltip("productType")}
              onReset={() => handleResetToDefault("productType")}
              isSearchable={false}
              menuPlacement="top"
            />

            <SelectFieldRow
              label="Preferred Vendor"
              value={selectedPreferredVendor}
              onChange={(option) =>
                setFormData({ ...formData, preferredVendorId: option?.value ?? null })
              }
              options={props.commonVendors?.map((v) => ({ value: v.id, label: v.name })) ?? []}
              placeholder={
                props.commonVendors?.length === 0
                  ? "No common vendors"
                  : metadataForEdit?.fields.preferredVendorId?.isMultiple &&
                      formData.preferredVendorId !== null
                    ? "Mixed values"
                    : undefined
              }
              isDisabled={props.commonVendors.length === 0}
              fieldData={metadataForEdit?.fields.preferredVendorId}
              badges={getFieldBadges("preferredVendorId")}
              erpIndicator={getErpIndicator("preferredVendorId")}
              showResetButton={shouldShowResetButton("preferredVendorId")}
              resetTooltip={getResetTooltip("preferredVendorId")}
              onReset={() => handleResetToDefault("preferredVendorId")}
              isClearable
              isSearchable
              menuPlacement="top"
            />
          </Card>
        </div>

        <Card className="flex shrink-0 gap-3 bg-component min-h-[58px] px-4 py-3">
          <Switch
            checked={formData.ignore}
            disabled={!metadataForEdit.fields.ignore?.isEditable}
            onCheckedChange={(checked) => setFormData({ ...formData, ignore: checked })}
          />
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <Label className="font-medium text-fg-base">
                Hide {props.selectedVariantCount > 1 ? "variants" : "variant"}
              </Label>
              <InfoTooltip
                className="h-4 w-4"
                containerClassName="w-[400px]"
                body={
                  <div>
                    Hidden products will not be visible on the platform. However, they can still be
                    accessed and modified via the <b>Product Management</b> tab.
                  </div>
                }
              />
            </div>
            <p className="text-fg-subtle text-xs">
              {hideHelperText(
                metadataForEdit.fields.ignore,
                needsRemovalConsent,
                props.selectedVariantCount,
              )}
            </p>
            {needsRemovalConsent && (
              <div className="flex items-center gap-2 pt-1">
                <Checkbox
                  id="hide-remove-from-collections"
                  checked={removeFromCollections}
                  onCheckedChange={(checked) => setRemoveFromCollections(checked === true)}
                />
                <Label htmlFor="hide-remove-from-collections" className="text-xs">
                  Also remove from {hideCollectionNames.join(", ")}
                </Label>
              </div>
            )}
          </div>
        </Card>
      </div>

      <DialogFooterV2 className="flex justify-end gap-3 bg-subtle py-3 px-4">
        <DialogClose>
          <Button asChild variant="transparent">
            Cancel
          </Button>
        </DialogClose>
        <Button
          requires="edit"
          allowedTitle={saveBlockedReason}
          onClick={handleUpdateMetadata}
          disabled={!!saveBlockedReason || saveMetadata.isPending}
          loading={saveMetadata.isPending}
        >
          Save {props.selectedVariantCount}{" "}
          {props.selectedVariantCount > 1 ? "variants" : "variant"}
        </Button>
      </DialogFooterV2>
    </>
  )
}
